import React, { useState, useEffect, useCallback } from 'react'
import { Button, Modal, ModalBody } from 'reactstrap'
import { PUBSUB_GROUP_EVENT } from '@common'

/**
 * PaymentEventModal Component
 *
 * Handles different payment events with appropriate UI/UX:
 * - PAYMENT_LATER: Shows order number and asks customer to return later
 * - PAYMENT_REQUEST: Redirects to payment QR code page
 * - PAYMENT_SUCCESS: Shows order number and thank you message
 *
 * Props:
 * - isVisible: boolean - controls modal visibility
 * - eventType: string - type of payment event
 * - eventData: object - event payload data
 * - onClose: function - callback when modal is closed
 * - onPaymentRequest: function - callback for payment request (redirect)
 */
const PaymentEventModal = ({
    isVisible = false,
    eventType = null,
    eventData = {},
    onClose = () => {},
    onPaymentRequest = () => {},
}) => {
    const [autoCloseTimer, setAutoCloseTimer] = useState(null)

    // Auto-close timer for success and later events
    useEffect(() => {
        const validEventTypes = [
            PUBSUB_GROUP_EVENT.PAYMENT_SUCCESS,
            PUBSUB_GROUP_EVENT.PAYMENT_LATER,
            PUBSUB_GROUP_EVENT.PAYMENT_CANCEL,
        ]

        if (isVisible && validEventTypes.includes(eventType)) {
            const timer = setTimeout(() => {
                onClose()
            }, 10000)

            setAutoCloseTimer(timer)

            return () => {
                if (timer) clearTimeout(timer)
            }
        }

        return () => {
            if (autoCloseTimer) {
                clearTimeout(autoCloseTimer)
                setAutoCloseTimer(null)
            }
        }
    }, [isVisible, eventType, onClose])

    // Auto-redirect timer for payment requests
    useEffect(() => {
        if (isVisible && eventType === PUBSUB_GROUP_EVENT.PAYMENT_REQUEST) {
            onPaymentRequest(eventData)
        }
    }, [isVisible, eventType, eventData, onPaymentRequest])

    const handleClose = useCallback(() => {
        if (autoCloseTimer) {
            clearTimeout(autoCloseTimer)
            setAutoCloseTimer(null)
        }
        onClose()
    }, [autoCloseTimer, onClose])

    const renderContent = () => {
        switch (eventType) {
            case PUBSUB_GROUP_EVENT.PAYMENT_LATER:
                return (
                    <div className="text-center py-5">
                        <div className="mb-4">
                            <div className="display-1 mb-3">
                                <i className="bx bx-time-five text-warning" style={{ fontSize: '5rem' }}></i>
                            </div>
                            <h2 className="display-6 fw-bold mb-3">
                                {eventData.meta?.isFromQuickCheckout ? 'Please Pay at Drink Stall Cashier' : 'Payment Later'}
                            </h2>
                            <p className="fs-4 text-muted mb-4">Please remember your order number</p>
                        </div>

                        <div className="bg-light rounded-4 p-4 mb-4">
                            <div className="text-muted mb-2" style={{ fontSize: '1.2rem' }}>
                                Order Number
                            </div>
                            <div className="display-4 fw-bold text-primary">
                                #{eventData.number || eventData.orderId || 'N/A'}
                            </div>
                        </div>

                        {/* <p className="fs-5 text-muted mb-4">Please return to complete your payment when ready</p> */}

                        <Button
                            color="primary"
                            size="lg"
                            onClick={handleClose}
                            style={{ fontSize: '1.2rem', padding: '12px 32px' }}
                        >
                            OK, Got It
                        </Button>
                    </div>
                )

            case PUBSUB_GROUP_EVENT.PAYMENT_REQUEST:
                return (
                    <div className="text-center py-5">
                        <div className="mb-4">
                            <div className="display-1 mb-3">
                                {' '}
                                <i className="bx bx-credit-card text-primary"></i>
                            </div>
                            <h2 className="display-6 fw-bold mb-3">Payment Required</h2>
                            <p className="fs-4 text-muted mb-4">Redirecting to payment page in a moment...</p>
                        </div>

                        <div className="bg-light rounded-4 p-4 mb-4">
                            <div className="text-muted mb-2" style={{ fontSize: '1.2rem' }}>
                                Amount
                            </div>
                            <div className="display-5 fw-bold text-primary">
                                S$ {(eventData.totalAmount || eventData.amount || 0).toFixed(2)}
                            </div>
                        </div>

                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                )

            case PUBSUB_GROUP_EVENT.PAYMENT_SUCCESS:
                return (
                    <div className="text-center py-5">
                        <div className="mb-4">
                            <div className="display-1 mb-3">
                                <i className="bx bx-check-circle text-primary"></i>
                            </div>
                            <h2 className="display-6 fw-bold mb-3 text-primary">Payment Successful!</h2>
                            <p className="fs-4 text-muted mb-4">Thank you for your order</p>
                        </div>

                        <div className="bg-primary bg-opacity-10 rounded-4 p-4 mb-4 border-opacity-25">
                            <div className="text-muted mb-2" style={{ fontSize: '1.2rem' }}>
                                Order Number
                            </div>
                            <div className="display-4 fw-bold text-primary">
                                #{eventData.number || eventData.orderId || 'N/A'}
                            </div>
                        </div>

                        {/* <p className="fs-5 text-muted mb-4">
                            Your order is being prepared. Please wait for your number to be called.
                        </p> */}

                        <Button
                            color="primary"
                            size="lg"
                            onClick={handleClose}
                            style={{ fontSize: '1.2rem', padding: '12px 32px' }}
                        >
                            Thank You!
                        </Button>
                    </div>
                )

            case PUBSUB_GROUP_EVENT.PAYMENT_FAILED:
                return (
                    <div className="text-center py-5">
                        <div className="mb-4">
                            <div className="display-1 mb-3">
                                <i className="bx bx-x-circle text-danger"></i>
                            </div>
                            <h2 className="display-6 fw-bold mb-3 text-danger">Payment Failed!</h2>
                            <p className="fs-4 text-muted mb-4">Please try again or inform the cashier</p>
                        </div>

                        <Button
                            color="danger"
                            size="lg"
                            onClick={handleClose}
                            style={{ fontSize: '1.2rem', padding: '12px 32px' }}
                        >
                            Close
                        </Button>
                    </div>
                )

            case PUBSUB_GROUP_EVENT.PAYMENT_CANCEL:
                return (
                    <div className="text-center py-5">
                        <div className="mb-4">
                            <div className="display-1 mb-3">
                                <i className="bx bx-x-circle text-danger"></i>
                            </div>
                            <h2 className="display-6 fw-bold mb-3 text-danger">Payment Cancelled!</h2>
                            <p className="fs-4 text-muted mb-4">Please try again or inform the cashier</p>
                        </div>

                        <Button
                            color="danger"
                            size="lg"
                            onClick={handleClose}
                            style={{ fontSize: '1.2rem', padding: '12px 32px' }}
                        >
                            Close
                        </Button>
                    </div>
                )

            default:
                return (
                    <div className="text-center py-5">
                        <p className="fs-4 text-muted">Unknown payment event</p>
                        <Button color="secondary" onClick={handleClose}>
                            Close
                        </Button>
                    </div>
                )
        }
    }

    if (!isVisible || !eventType) {
        return null
    }

    return (
        <Modal
            isOpen={isVisible}
            toggle={handleClose}
            centered
            size="lg"
            backdrop="static"
            keyboard={false}
            style={{ zIndex: 10000 }}
        >
            <ModalBody className="p-0">{renderContent()}</ModalBody>
        </Modal>
    )
}

export default PaymentEventModal
